import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

/**
 * Converte ArrayBuffer para base64 de forma eficiente (para arquivos grandes)
 */
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 8192; // Processar em chunks para evitar stack overflow
  
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, chunk);
  }
  
  return btoa(binary);
};

/**
 * Função para fazer download de arquivos compatível com iOS e Web
 * @param {string} url - URL do arquivo para download
 * @param {string} filename - Nome do arquivo
 * @returns {Promise<void>}
 */
export const downloadFile = async (url, filename) => {
  try {
    // Verificar se está rodando no Capacitor (app móvel)
    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();
    
    console.log('Download iniciado:', { url, filename, isNative, platform });

    if (isNative && (platform === 'ios' || platform === 'android')) {
      console.log(`Usando método ${platform} para download`);
      
      // Para iOS e Android, usar Filesystem e Share
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
      }

      console.log('Arquivo baixado, convertendo para base64...');
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const base64Data = arrayBufferToBase64(arrayBuffer);

      console.log('Salvando arquivo no sistema de arquivos...');
      // Salvar arquivo no sistema de arquivos
      const writeResult = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      });

      console.log('Arquivo salvo com sucesso:', writeResult.uri);

      // Compartilhar o arquivo (abre o menu de compartilhamento)
      try {
        console.log('Abrindo menu de compartilhamento...');
        await Share.share({
          title: 'Baixar arquivo',
          text: filename,
          url: writeResult.uri,
          dialogTitle: 'Salvar arquivo'
        });
        console.log('Menu de compartilhamento aberto com sucesso');
      } catch (shareError) {
        // Se o compartilhamento falhar, pelo menos o arquivo foi salvo
        console.log('Erro ao compartilhar (mas arquivo foi salvo):', shareError);
        console.log('Arquivo salvo em:', writeResult.uri);
        alert(`Arquivo salvo com sucesso! Você pode encontrá-lo em: ${writeResult.uri}`);
      }
    } else {
      // Para web, usar o método tradicional
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }
  } catch (error) {
    console.error('Erro ao fazer download:', error);
    throw error;
  }
};

/**
 * Função para fazer download de blob (já baixado)
 * @param {Blob} blob - Blob do arquivo
 * @param {string} filename - Nome do arquivo
 * @returns {Promise<void>}
 */
export const downloadBlob = async (blob, filename) => {
  try {
    // Verificar se está rodando no Capacitor (app móvel)
    const isNative = Capacitor.isNativePlatform();
    const platform = Capacitor.getPlatform();
    
    console.log('Download de blob iniciado:', { filename, isNative, platform });

    if (isNative && (platform === 'ios' || platform === 'android')) {
      console.log(`Usando método ${platform} para download de blob`);
      
      // Para iOS e Android, converter blob para base64 e salvar
      const arrayBuffer = await blob.arrayBuffer();
      const base64Data = arrayBufferToBase64(arrayBuffer);

      console.log('Salvando arquivo no sistema de arquivos...');
      // Salvar arquivo no sistema de arquivos
      const writeResult = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents,
        recursive: true
      });

      console.log('Arquivo salvo com sucesso:', writeResult.uri);

      // Compartilhar o arquivo
      try {
        console.log('Abrindo menu de compartilhamento...');
        await Share.share({
          title: 'Baixar arquivo',
          text: filename,
          url: writeResult.uri,
          dialogTitle: 'Salvar arquivo'
        });
        console.log('Menu de compartilhamento aberto com sucesso');
      } catch (shareError) {
        console.log('Erro ao compartilhar (mas arquivo foi salvo):', shareError);
        console.log('Arquivo salvo em:', writeResult.uri);
        alert(`Arquivo salvo com sucesso! Você pode encontrá-lo em: ${writeResult.uri}`);
      }
    } else {
      // Para web, usar o método tradicional
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    }
  } catch (error) {
    console.error('Erro ao fazer download:', error);
    throw error;
  }
};

